import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import { ComponentProps, useEffect, useState } from 'react';

import { Inline } from '../../../../../design-system/inline/inline';
import { Stack } from '../../../../../design-system/stack/stack';
import { InvoiceEvidenceView } from '../../../../../types/entity-types';
import { ReviewMatchesTable } from './review-matches-table';
import { reviewMatchesTableColumnsDefinition } from './review-matches-table-columns-definition';
import { SelectedRowApproveButton } from './selected-row-approve-button';
import { TableFilterSelectAll } from './table-filter-select-all';
import { TableFilters } from './table-filters';
import { TableSorting } from './table-sorting';

type ReviewMatchesTableWrapperProps = {
  evidences: InvoiceEvidenceView[];
  approvingEvidenceId?: string[];
  onSelectMenuItem: ComponentProps<typeof ReviewMatchesTable>['onSelectMenuItem'];
  onReviewInvoice: (invoiceId: string) => void;
  onApproveInvoice: (invoiceIds: string[]) => Promise<void>;
  onDataSorted?: (sortedEvidence: InvoiceEvidenceView[]) => void;
};

export const ReviewMatchesTableWrapper = ({
  evidences,
  approvingEvidenceId,
  onSelectMenuItem,
  onReviewInvoice,
  onApproveInvoice,
  onDataSorted,
}: ReviewMatchesTableWrapperProps) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const table = useReactTable({
    data: evidences,
    columns: reviewMatchesTableColumnsDefinition,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableRowSelection: (row) => !!row.original.transactionId,
    initialState: {
      sorting: [{ id: 'billingDates', desc: true }],
    },
    state: {
      rowSelection,
    },
    meta: {
      onSelectMenuItem,
      onReviewInvoice,
      onApproveInvoice,
      approvingEvidenceId,
    },
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => row.id,
  });

  const selectedEvidenceIds = Object.keys(rowSelection);

  useEffect(() => {
    if (!onDataSorted) {
      return;
    }

    const sortedRows = table.getRowModel().rows.map((row) => row.original);

    onDataSorted(sortedRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasVisibleRows = table.getPrePaginationRowModel().rows.length > 0;

  return (
    <>
      <Stack space={1} align="stretch" className="pt-4">
        <Inline className="w-full pl-4">
          {hasVisibleRows && <TableFilterSelectAll table={table} />}

          <Inline space={1} className="ml-auto">
            <TableSorting table={table} />

            <TableFilters table={table} />
          </Inline>
        </Inline>

        {hasVisibleRows && <ReviewMatchesTable table={table} onSelectMenuItem={onSelectMenuItem} />}

        {!hasVisibleRows && (
          <Stack space={1} align="center" className="w-full py-24">
            <p className="text-center text-sm leading-5 font-medium tracking-tight text-black">
              Your filters returned no results
            </p>
          </Stack>
        )}
      </Stack>

      {selectedEvidenceIds.length > 0 && (
        <SelectedRowApproveButton
          selectedRowCount={selectedEvidenceIds.length}
          isLoading={approvingEvidenceId ? approvingEvidenceId.length > 0 : false}
          onApprove={async () => {
            await onApproveInvoice(selectedEvidenceIds);
            setRowSelection({});
            table.resetColumnFilters();
          }}
          onClose={() => setRowSelection({})}
        />
      )}
    </>
  );
};
